import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Link2, Users2, Pencil, Trash2, X, FileSignature, ArrowRight, Eye, Upload, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { EmailLink, PhoneLink } from "../components/ContactLinks";
import ClientEdit from "../components/ClientEdit";
import PickPlanModal from "../components/PickPlanModal";

// Display name for the tier one step up from the agent's current one —
// matches the backend TIERS catalogue in `app/services/plans.py`. Free
// falls back to Growth (the entry paid plan) because "next tier" only
// gets consulted when we know the current plan has a cap.
const NEXT_TIER_NAME = { free: "Growth", growth: "Master", team: "Pro" };

// Keys the /portal shell reads. Kept in sync with lib/api.js:portal so
// impersonation slots into the same session mechanism a real client uses.
const PORTAL_TOKEN_KEY = "realtor_os_portal_token";
const PORTAL_AGENT_KEY = "realtor_os_portal_agent";
const PORTAL_CLIENT_KEY = "realtor_os_portal_client";
const PORTAL_BRAND_KEY = "realtor_os_portal_brand";
// Extras that only apply to agent-impersonation mode.
const IMPERSONATE_RETURN_KEY = "realtor_os_impersonate_return_path";
const IMPERSONATE_ACTIVE_KEY = "realtor_os_impersonate_active";

export default function Clients() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const [clients, setClients] = useState([]);
  // Distinguish "still fetching /clients" from "fetch done, list empty" so
  // agents don't see "No clients yet" flash on every navigation while the
  // request is in flight. Starts true so the skeleton renders on mount.
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    source: "",
    client_type: "buyer",
    property_interest: "",
    budget_min: "",
    budget_max: "",
    notes: "",
  });
  const [editing, setEditing] = useState(null); // client being edited
  const [txModal, setTxModal] = useState(null); // { client_id, client_name }
  const [txForm, setTxForm] = useState({ side: "buyer", property_address: "", acceptance_date: "" });
  const [txBusy, setTxBusy] = useState(false);
  // Plan snapshot drives the "Add client" cap gate. Fetched from
  // /billing/me on mount and re-fetched after every successful create
  // so the button flips locked the moment the agent hits their cap.
  // Founders on ADMIN_EMAILS come back with `exempt: true` — treated as
  // "no cap" without hardcoding email lists here.
  const [plan, setPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const loadPlan = async () => {
    try {
      const { data } = await api.get("/billing/me");
      setPlan(data?.plan || null);
    } catch (e) { /* fail-open: don't block Adds on a transient 5xx */ }
  };
  useEffect(() => { loadPlan(); }, []);

  // Derive gate state. Broker/Pro (`client_limit: null`) and founders
  // (`exempt: true`) both come back with `client_limit_reached: false`
  // from the backend, so this one flag covers both allow paths.
  const capReached = !!plan?.client_limit_reached;
  const nextTierName = NEXT_TIER_NAME[plan?.tier] || "Growth";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } finally {
      // Always clear the flag, even on 4xx/5xx, so the empty state can
      // finally render instead of stalling on the skeleton indefinitely.
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // When arriving via ?highlight=<clientId> (e.g. from a transaction), scroll
  // the matching row into view and pulse a ring for ~2.5s so the agent
  // instantly sees which client they landed on.
  useEffect(() => {
    if (!highlightId || clients.length === 0) return;
    const el = document.querySelector(`[data-testid="client-row-${highlightId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("client-row-highlight");
      const timer = setTimeout(() => el.classList.remove("client-row-highlight"), 2500);
      return () => clearTimeout(timer);
    }
  }, [highlightId, clients]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Compose full_name from first + last so both entry surfaces (this
      // inline form + the API-only POST) end up with the same rendering.
      payload.full_name = [payload.first_name, payload.last_name]
        .map(s => (s || "").trim())
        .filter(Boolean)
        .join(" ");
      ["budget_min", "budget_max"].forEach(k => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      await api.post("/clients", payload);
      setForm({
        first_name: "", last_name: "", email: "", phone: "", address: "",
        source: "", client_type: "buyer", property_interest: "",
        budget_min: "", budget_max: "", notes: "",
      });
      setCreating(false);
      toast.success("Client added");
      load();
      // Refresh the plan snapshot — this may have been the client that
      // pushed the agent over their cap; the button should now lock.
      loadPlan();
    } catch (err) {
      // Backend enforce_client_limit returns 402 PLAN_LIMIT_REACHED with
      // the machine-readable detail. Pop the Pick-Plan modal so the fix
      // is one click, not a copy-paste of "upgrade in settings".
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 402 && d?.code === "PLAN_LIMIT_REACHED") {
        toast.error(d.message || "You have reached the client limit for your current plan.");
        setShowPlanModal(true);
        loadPlan();
        return;
      }
      toast.error(err?.response?.data?.detail || "Failed to add");
    }
  };

  const generateLink = async (clientId, sendEmail = false) => {
    try {
      const { data } = await api.post(`/clients/${clientId}/magic-link`, { origin_url: window.location.origin, send_email: sendEmail });
      const url = `${window.location.origin}/portal/verify?token=${data.token}`;
      if (sendEmail && data.emailed) {
        toast.success("Magic link emailed to the client");
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Magic link copied to clipboard · expires in 7 days");
      }
    } catch (err) {
      // The disclosure-compliance guard 400s with a structured detail —
      // surface its message verbatim so the agent knows exactly which
      // Settings fields to complete before retrying.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "disclosure_incomplete") {
        toast.error(detail.message, {
          duration: 8000,
          action: {
            label: "Open Settings",
            onClick: () => window.location.assign("/settings#brokerage-licensing"),
          },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to generate");
      }
    }
  };

  const saveEdit = null; // replaced by <ClientEdit /> modal — kept as
  // a no-op reference so any stray caller fails loudly rather than
  // silently double-saving through a stale closure.
  void saveEdit;
  const del = async (id) => {
    if (!window.confirm("Delete this client? Their portal magic-links stop working immediately.")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  /**
   * Preview the branded portal as this client. Mints a short-lived JWT via
   * the backend, drops it into localStorage under the same keys the portal
   * shell reads, then navigates to /portal. We also stash the current agent
   * path so the "Back to agent view" banner can return to exactly where we
   * were (usually /clients, but future entry points might not be).
   */
  const previewAsClient = async (client) => {
    try {
      const { data } = await api.post(`/clients/${client.id}/impersonate-portal`);
      localStorage.setItem(PORTAL_TOKEN_KEY, data.token);
      localStorage.setItem(PORTAL_CLIENT_KEY, JSON.stringify(data.client));
      localStorage.setItem(PORTAL_AGENT_KEY, JSON.stringify(data.agent));
      if (data.brokerage) {
        localStorage.setItem("realtor_os_portal_brokerage", JSON.stringify(data.brokerage));
      } else {
        localStorage.removeItem("realtor_os_portal_brokerage");
      }
      // Cache the broker-disclosure payload the same way PortalVerify does
      // after a real magic-link exchange, so the impersonation preview
      // renders the Compact block on /portal/messages too.
      if (data.disclosure) {
        localStorage.setItem("realtor_os_portal_disclosure", JSON.stringify(data.disclosure));
      } else {
        localStorage.removeItem("realtor_os_portal_disclosure");
      }
      if (data.agent?.brand_color) {
        localStorage.setItem(PORTAL_BRAND_KEY, data.agent.brand_color);
      }
      localStorage.setItem(IMPERSONATE_RETURN_KEY, window.location.pathname);
      localStorage.setItem(IMPERSONATE_ACTIVE_KEY, "1");
      nav("/portal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open client preview");
    }
  };

  const openTxModal = (c) => {
    setTxForm({ side: "buyer", property_address: "", acceptance_date: "" });
    setTxModal({ client_id: c.id, client_name: c.full_name });
  };

  const submitTx = async (e) => {
    e.preventDefault();
    setTxBusy(true);
    const payload = { side: txForm.side, client_id: txModal.client_id };
    if (txForm.property_address) payload.property_address = txForm.property_address;
    if (txForm.acceptance_date) payload.acceptance_date = txForm.acceptance_date;
    try {
      const { data } = await api.post("/transactions", payload);
      toast.success(`Transaction created${data.deadlines?.length ? ` · ${data.deadlines.length} contract deadlines` : ""}`);
      setTxModal(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create transaction");
    } finally { setTxBusy(false); }
  };

  const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand";
  const labelCls = "text-xs font-medium text-slate-600 uppercase tracking-wider";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-slate-900">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">Buyers and sellers who see their transaction in the branded portal. Magic-link sign-in, no password.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/clients/import"
            className={`inline-flex flex-1 sm:flex-none justify-center items-center gap-2 rounded-md px-3 sm:px-4 py-2 text-sm font-semibold ${capReached ? "text-slate-400 bg-slate-100 pointer-events-none" : "text-slate-700 bg-slate-100 hover:bg-slate-200"}`}
            aria-disabled={capReached}
            title={capReached ? `You have reached the client limit for your current plan. Upgrade to the ${nextTierName} to add more clients.` : "Import clients from a CSV"}
            data-testid="import-clients-btn"
          >
            <Upload className="h-4 w-4" /> <span>Import CSV</span>
          </Link>
          {capReached ? (
            <button
              onClick={() => setShowPlanModal(true)}
              className="inline-flex flex-1 sm:flex-none justify-center items-center gap-2 rounded-md px-3 sm:px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700"
              title={`You have reached the client limit for your current plan. Upgrade to the ${nextTierName} to add more clients.`}
              data-testid="new-client-btn"
            >
              <Lock className="h-4 w-4" /> <span className="whitespace-nowrap">Add client · locked</span>
            </button>
          ) : (
            <button onClick={() => setCreating(true)} className="inline-flex flex-1 sm:flex-none justify-center items-center gap-2 rounded-md px-3 sm:px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="new-client-btn">
              <Plus className="h-4 w-4" /> <span>Add client</span>
            </button>
          )}
        </div>
      </div>

      {/* Cap-reached upgrade prompt — exact copy per spec. Sits above the
          client list so the agent sees WHY the button is disabled without
          hovering for the tooltip. Founders on ADMIN_EMAILS never see
          this row (backend flags them `exempt: true`). */}
      {capReached && (
        <div className="mb-6 rounded-lg border border-rose-300 bg-rose-50 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="clients-cap-prompt">
          <div className="flex items-start gap-3 min-w-0">
            <Lock className="h-5 w-5 shrink-0 text-rose-700 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-rose-900">
                You have reached the client limit for your current plan.
              </div>
              <div className="mt-0.5 text-sm text-rose-900">
                Upgrade to the <b>{nextTierName}</b> to add more clients.
                <span className="text-rose-800/80"> ({plan?.client_count}/{plan?.client_limit} clients on {plan?.tier_name})</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowPlanModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-sm font-semibold shrink-0"
            data-testid="clients-cap-upgrade-btn"
          >
            Upgrade to {nextTierName} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {creating && (
        <form onSubmit={create} className="rounded-lg border border-slate-200 bg-white p-6 mb-6" data-testid="new-client-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First name *</label>
              <input required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className={inputCls} data-testid="client-first-name-input" />
            </div>
            <div>
              <label className={labelCls}>Last name *</label>
              <input required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className={inputCls} data-testid="client-last-name-input" />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="client-email-input" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="client-phone-input" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Address</label>
              <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputCls} placeholder="Home / mailing address" data-testid="client-address-input" />
            </div>
            <div>
              <label className={labelCls}>Source</label>
              <input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className={inputCls} placeholder="Zillow, referral, open house…" data-testid="client-source-input" />
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select value={form.client_type} onChange={e => setForm({ ...form, client_type: e.target.value })} className={inputCls} data-testid="client-type-select">
                <option value="buyer">Buyer</option><option value="seller">Seller</option><option value="both">Both</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Price min</label>
              <input type="number" value={form.budget_min} onChange={e => setForm({ ...form, budget_min: e.target.value })} className={inputCls} data-testid="client-budget-min-input" />
            </div>
            <div>
              <label className={labelCls}>Price max</label>
              <input type="number" value={form.budget_max} onChange={e => setForm({ ...form, budget_max: e.target.value })} className={inputCls} data-testid="client-budget-max-input" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Property interest</label>
              <textarea value={form.property_interest} onChange={e => setForm({ ...form, property_interest: e.target.value })} rows={2} className={inputCls} placeholder="Luxury single-family in Arcadia; pool required" data-testid="client-property-interest-input" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className={inputCls} data-testid="client-notes-input" />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-end gap-3">
            <button type="button" onClick={() => setCreating(false)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
            <button type="submit" className="rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="client-save-btn">Add client</button>
          </div>
        </form>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {loading && (
          // Skeleton rows during the initial /clients fetch. Matches the
          // real row height so the layout doesn't jump when data arrives.
          <div className="divide-y divide-slate-100" data-testid="clients-loading" aria-busy="true" aria-live="polite">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <div className="h-8 w-24 bg-slate-100 rounded" />
                  <div className="h-8 w-24 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
            <div className="sr-only">Loading clients…</div>
          </div>
        )}
        {!loading && clients.length === 0 && (
          <div className="p-12 text-center" data-testid="clients-empty">
            <Users2 className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-3 font-heading text-lg font-semibold text-slate-900">No clients yet</p>
          </div>
        )}
        {!loading && clients.map(c => (
          <div key={c.id} className="px-4 sm:px-6 py-4 border-b border-slate-100 last:border-b-0" data-testid={`client-row-${c.id}`}>
            {/* Card layout: on mobile stacks (info -> actions), on sm+ single row */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 sm:flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/clients/${c.id}`}
                    className="font-semibold text-slate-900 hover:underline truncate"
                    data-testid={`client-open-${c.id}`}
                  >
                    {c.full_name}
                  </Link>
                  {c.lead_id && (
                    <Link
                      to={`/leads/${c.lead_id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 border border-slate-200 rounded px-1.5 py-0.5"
                      data-testid={`client-lead-backlink-${c.id}`}
                      title={c.lead_name ? `From lead: ${c.lead_name}` : "From lead"}
                    >
                      From lead <ArrowRight className="h-2.5 w-2.5" />
                    </Link>
                  )}
                  {c.tx_count > 0 && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`client-tx-count-${c.id}`}>
                      {c.tx_count} tx
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-xs">
                  <EmailLink email={c.email} testId={`client-email-link-${c.id}`} className="text-xs" />
                  <PhoneLink phone={c.phone} testId={`client-phone-link-${c.id}`} className="text-xs" />
                </div>
              </div>

              {/* Action bar — mobile: grid of icon+label buttons; desktop: inline row */}
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-1.5 sm:flex-nowrap shrink-0">
                <button
                  onClick={() => openTxModal(c)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white rounded px-3 py-2 sm:py-1.5 col-span-2 sm:col-span-1"
                  style={{ background: "var(--primary-hex)" }}
                  data-testid={`client-start-tx-${c.id}`}
                >
                  <FileSignature className="h-3.5 w-3.5" /> Start transaction
                </button>
                <button
                  onClick={() => previewAsClient(c)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-slate-300 rounded px-3 py-2 sm:py-1.5 hover:bg-slate-50 text-slate-700"
                  title="Open the branded portal exactly as this client sees it"
                  data-testid={`client-preview-portal-${c.id}`}
                >
                  <Eye className="h-3.5 w-3.5" /> <span>View as client</span>
                </button>
                <button
                  onClick={() => generateLink(c.id, false)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-slate-300 rounded px-3 py-2 sm:py-1.5 hover:bg-slate-50 text-slate-700"
                  data-testid={`client-link-${c.id}`}
                >
                  <Link2 className="h-3.5 w-3.5" /> <span>Copy invite</span>
                </button>
                <button
                  onClick={() => generateLink(c.id, true)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-slate-300 rounded px-3 py-2 sm:py-1.5 hover:bg-slate-50 text-slate-700"
                  data-testid={`client-email-${c.id}`}
                >
                  <Link2 className="h-3.5 w-3.5" /> <span>Resend</span>
                </button>
                {/* Edit / Delete grouped so the row terminates cleanly on both breakpoints */}
                <div className="col-span-2 sm:col-span-1 flex items-center justify-end sm:justify-start gap-1 pl-1 sm:pl-1 sm:border-l sm:border-slate-200 sm:ml-1">
                  <button onClick={() => setEditing(c)} className="text-slate-400 hover:text-slate-900 p-2 sm:p-1 rounded hover:bg-slate-100" title="Edit" data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-700 p-2 sm:p-1 rounded hover:bg-slate-100" title="Delete" data-testid={`client-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ClientEdit
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* Pick-Plan modal — opens whenever the agent hits their cap
          (either by clicking the disabled button or by triggering a 402
          from the API). Founders never reach this because `capReached`
          stays false for them. */}
      {showPlanModal && (
        <PickPlanModal
          onClose={() => { setShowPlanModal(false); loadPlan(); }}
        />
      )}

      {txModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="start-tx-modal">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900">Start transaction</h2>
                <p className="text-xs text-slate-500 mt-0.5">For <span className="font-semibold text-slate-800">{txModal.client_name}</span> — access + contract deadlines are set up automatically.</p>
              </div>
              <button onClick={() => setTxModal(null)} className="text-slate-400 hover:text-slate-900 p-1"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submitTx} className="mt-5 space-y-3">
              <div>
                <label className={labelCls}>Side</label>
                <select value={txForm.side} onChange={e => setTxForm({...txForm, side: e.target.value})} className={inputCls} data-testid="start-tx-side">
                  <option value="buyer">Buyer</option><option value="seller">Seller</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Property address</label>
                <input value={txForm.property_address} onChange={e => setTxForm({...txForm, property_address: e.target.value})} className={inputCls} placeholder="123 Main St, Anytown" data-testid="start-tx-address" />
              </div>
              <div>
                <label className={labelCls}>Acceptance date <span className="text-slate-400 normal-case">· optional, triggers contract deadlines</span></label>
                <input type="date" value={txForm.acceptance_date} onChange={e => setTxForm({...txForm, acceptance_date: e.target.value})} className={inputCls} data-testid="start-tx-acceptance" />
              </div>
              <div className="pt-2 flex items-center justify-end gap-3">
                <button type="button" onClick={() => setTxModal(null)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
                <button type="submit" disabled={txBusy} className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "var(--primary-hex)" }} data-testid="start-tx-submit">
                  {txBusy ? "Creating…" : "Create transaction"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
